import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Download, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const Stat = ({ label, value, sub, testId }) => (
  <div className="bg-white rounded-2xl shadow-card p-4">
    <p className="text-xs text-text-mid uppercase tracking-wide">{label}</p>
    <p className="text-2xl font-bold text-navy mt-1" data-testid={testId}>{value}</p>
    {sub && <p className="text-[11px] text-text-mid mt-1">{sub}</p>}
  </div>
);

export default function AdminMetricsTab() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      const res = await api.get('/admin/metrics/summary', { params, headers: getAuthHeader() });
      setSummary(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load metrics');
    }
    setLoading(false);
  }, [start, end]);

  useEffect(() => { fetchSummary(); /* eslint-disable-next-line */ }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const qs = new URLSearchParams();
      if (start) qs.set('start', start);
      if (end) qs.set('end', end);
      const res = await fetch(`${apiUrl}/api/admin/metrics/users/export?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { toast.error('Export failed'); setExporting(false); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user_metrics_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('User metrics CSV exported');
    } catch {
      toast.error('Export failed');
    }
    setExporting(false);
  };

  const att = summary?.attendance_in_range;

  return (
    <div data-testid="admin-metrics-tab">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <h2 className="font-display text-2xl text-navy">Member Metrics</h2>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-xl h-9" data-testid="metrics-start-date" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-xl h-9" data-testid="metrics-end-date" />
          </div>
          <Button onClick={fetchSummary} variant="outline" className="rounded-full h-9" data-testid="metrics-apply-btn">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="bg-teal hover:bg-teal-deep rounded-full h-9"
            data-testid="metrics-export-csv-btn"
          >
            {exporting ? <Loader2 size={15} className="animate-spin mr-2" /> : <Download size={15} className="mr-2" />}
            Export CSV
          </Button>
        </div>
      </div>

      {!summary ? (
        <p className="text-text-mid py-10 text-center">{loading ? 'Loading…' : 'No data'}</p>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Total Users" value={summary.total_users}
              sub={`${summary.users_by_tier.connect} Connect · ${summary.users_by_tier.thrive} Thrive`}
              testId="metric-total-users" />
            <Stat label="New Signups (range)" value={summary.new_signups_in_range.total}
              sub={`${summary.new_signups_in_range.connect} Connect · ${summary.new_signups_in_range.thrive} Thrive`}
              testId="metric-new-signups" />
            <Stat label="Active Subscribers" value={summary.active_subscriber_count}
              sub={summary.comped_excluded_from_mrr ? `${summary.comped_excluded_from_mrr} comped (promo/founder) excluded` : undefined}
              testId="metric-active-subs" />
            <Stat label="Current MRR" value={`$${Number(summary.current_mrr).toFixed(2)}`} testId="metric-mrr" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Conversions (range)" value={summary.conversions_in_range}
              sub={`${summary.conversion_rate_pct}% conversion rate`} testId="metric-conversions" />
            <Stat label="Via Checkout Upgrade" value={summary.conversions_via_checkout_upgrade}
              sub="workshop upgrade component" testId="metric-upgrade-conversions" />
            <Stat label="Median Events Before Converting" value={summary.median_events_attended_before_conversion}
              testId="metric-median-events" />
            <Stat label="Repeat Attendee Rate" value={`${summary.repeat_rate_pct}%`}
              sub="attended a second event" testId="metric-repeat-rate" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Gathering Attendance" value={(att?.gathering?.connect || 0) + (att?.gathering?.thrive || 0)}
              sub={`${att?.gathering?.connect || 0} Connect · ${att?.gathering?.thrive || 0} Thrive`}
              testId="metric-gathering-attendance" />
            <Stat label="Workshop Attendance" value={(att?.workshop?.connect || 0) + (att?.workshop?.thrive || 0)}
              sub={`${att?.workshop?.connect || 0} Connect · ${att?.workshop?.thrive || 0} Thrive`}
              testId="metric-workshop-attendance" />
            <Stat label="Credit Redemption Rate" value={`${summary.workshop_credit_redemption_rate_pct}%`}
              sub="this month, active Thrive" testId="metric-credit-redemption" />
          </div>

          <p className="text-xs text-text-light">
            CSV export includes one row per user with tier, pricing, attendance history, conversion tracking, credits, and guest-pass status. Date range filters by signup date on the CSV and by activity date on the summary.
          </p>
        </div>
      )}
    </div>
  );
}
